import { computed, Injectable, signal } from '@angular/core';

/**
 * Manages an ordered queue of videos eligible for autoplay.
 *
 * Only the first video in the queue (the "active" video) should play.
 * When videos enter the viewport, they're added to the queue.
 * When they leave, they're removed. If the active video is removed,
 * the next video in the queue becomes active and starts playing.
 *
 * @example
 * ```
 * const videoService = inject(VideoService);
 *
 * // Add videos to the queue (order is preserved)
 * videoService.addVideoToQueue('video1');
 * videoService.addVideoToQueue('video2');
 *
 * // Only 'video1' is active
 * videoService.activeVideo(); // 'video1'
 *
 * // Remove 'video1', now 'video2' becomes active
 * videoService.removeVideoFromQueue('video1');
 * videoService.activeVideo(); // 'video2'
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class VideoService {
  private readonly _queue = signal<string[]>([]);

  /** The full queue of videos waiting to play (readonly) */
  readonly queue = this._queue.asReadonly();

  /** The currently active video ID (first in queue), or null if queue is empty */
  readonly activeVideo = computed(() => this._queue()[0] ?? null);

  /**
   * Clears all videos from the queue.
   */
  destroy(): void {
    this._queue.set([]);
  }

  /**
   * Adds a video ID to the end of the queue.
   *
   * @param videoId - The video identifier to add
   *
   * @remarks
   * - Empty or falsy `videoId` values are silently ignored
   * - If the video is already in the queue, it won't be added again
   */
  addVideoToQueue(videoId: string): void {
    if (!videoId) return;

    this._queue.update((queue) => {
      if (queue.includes(videoId)) return queue;
      return [...queue, videoId];
    });
  }

  /**
   * Removes a video ID from the queue.
   *
   * @param videoId - The video identifier to remove
   *
   * @remarks
   * If the removed video was active (first in queue), the next video becomes active.
   */
  removeVideoFromQueue(videoId: string): void {
    if (!videoId) return;

    this._queue.update((queue) => {
      const index = queue.indexOf(videoId);
      if (index === -1) return queue;
      return queue.filter((id) => id !== videoId);
    });
  }
}
